import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { formatarMoedaBrasileira } from "../../utils/formatarMoedaBrasileira";

const formatPercent = (value) =>
    value != null ? `${Number(value).toFixed(2)}%` : "—";

const safeNumber = (value) =>
    value != null ? Number(value).toFixed(2) : "—";

function RankingTab({ comissoes, onRowSelected }) {
    const [sortKey, setSortKey] = useState("valorTotalReceber");
    const [filterCargo, setFilterCargo] = useState("todos");

    const cargos = useMemo(() => {
        const set = new Set(comissoes.map(c => c.funcionarioCargo ?? "Outros"));
        return ["todos", ...Array.from(set)];
    }, [comissoes]);

    const ranked = useMemo(() => {
        let list = filterCargo === "todos" ? comissoes : comissoes.filter(c => c.funcionarioCargo === filterCargo);

        const getVal = (c) => {
            if (sortKey === "valorTotalReceber")
                return c.valorTotalReceber ?? 0;

            if (sortKey === "percentualConclusao")
                return c.conclusaoRotaGreenmile?.percentualConclusao
                    ?? c.percentualConclusao
                    ?? 0;

            if (sortKey === "totalRotas")
                return c.resumo?.totalRotas
                    ?? c.totalRotas
                    ?? c.quantidadesEscalasDeRota
                    ?? 0;

            if (sortKey === "cubagem")
                return (c.cubagem?.cubosEntregue1Auxiliar ?? c.cubosEntregue1Auxiliar ?? 0)
                    + (c.cubagem?.cubosEntregue2Auxiliar ?? c.cubosEntregue2Auxiliar ?? 0);

            return 0;
        };

        return [...list].sort((a, b) => getVal(b) - getVal(a));
    }, [comissoes, sortKey, filterCargo]);

    const medalColors = ["bg-amber-400", "bg-gray-300", "bg-orange-400"];

    if (!comissoes.length) return (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <i className="fa fa-trophy text-5xl mb-4 opacity-30" />
            <p className="text-sm">Nenhum dado disponível.</p>
        </div>
    );

    return (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4">
            <div className="flex flex-wrap gap-3 mb-4 items-center">
                <div className="flex gap-2 flex-wrap">
                    {[
                        { key: "valorTotalReceber", label: "Valor a Receber" },
                        { key: "percentualConclusao", label: "% Conclusão" },
                        { key: "totalRotas", label: "Total Rotas" },
                        { key: "cubagem", label: "Cubagem" },
                    ].map(opt => (
                        <button
                            key={opt.key}
                            onClick={() => setSortKey(opt.key)}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150
                                ${sortKey === opt.key
                                    ? "bg-teal-600 text-white border-teal-600"
                                    : "bg-card text-muted-foreground border-border hover:border-teal-300"}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                <select
                    value={filterCargo}
                    onChange={e => setFilterCargo(e.target.value)}
                    className="ml-auto text-xs border border-input rounded-lg px-3 py-1.5 bg-card text-foreground"
                >
                    {cargos.map(c => <option key={c} value={c}>{c === "todos" ? "Todos os Cargos" : c}</option>)}
                </select>
            </div>

            <div className="space-y-2">
                {ranked.map((c, i) => {
                    const value = sortKey === "valorTotalReceber"
                        ? `R$ ${formatarMoedaBrasileira(c.valorTotalReceber)}`
                        : sortKey === "percentualConclusao"
                            ? formatPercent(
                                c.conclusaoRotaGreenmile?.percentualConclusao ?? c.percentualConclusao
                            )
                            : sortKey === "totalRotas"
                                ? (c.resumo?.totalRotas ?? c.totalRotas ?? c.quantidadesEscalasDeRota ?? "—")
                                : safeNumber(
                                    (c.cubagem?.cubosEntregue1Auxiliar ?? c.cubosEntregue1Auxiliar ?? 0)
                                    + (c.cubagem?.cubosEntregue2Auxiliar ?? c.cubosEntregue2Auxiliar ?? 0)
                                );

                    return (
                        <div key={c.funcionarioId} onClick={() => onRowSelected(c)} className="cursor-default flex items-center gap-3 bg-card border border-border rounded-xl p-3 shadow-card hover:shadow-soft transition-shadow duration-150">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-white text-xs flex-shrink-0
                                ${i < 3 ? medalColors[i] : "bg-muted text-muted-foreground"}`}>
                                {i + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-foreground truncate">{c.funcionarioNome} </p>
                                <p className="text-xs text-muted-foreground">{c.filialNome} · {c.funcionarioCargo} · {c.funcionarioCodigo}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                                <p className="font-bold text-teal-700 text-sm">{value}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </motion.div>
    );
}

export default RankingTab;